import React from "react";
import {
  Card,
  CardContent,
  Grid,
  ButtonGroup,
  Button,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  useMediaQuery,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ClearIcon from "@material-ui/icons/Clear";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { format as formatDate } from "date-fns";

const parseInputDate = (value) => (value ? new Date(`${value}T00:00:00`) : null);
const formatForInput = (date) =>
  date instanceof Date && !Number.isNaN(date?.getTime()) ? formatDate(date, "yyyy-MM-dd") : "";
const parseTypedDate = (value = "") => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.includes("_")) return null;

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  const dashMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const parts = slashMatch
    ? {
        month: Number(slashMatch[1]),
        day: Number(slashMatch[2]),
        year: Number(slashMatch[3].length === 2 ? `20${slashMatch[3]}` : slashMatch[3]),
      }
    : dashMatch
      ? {
          year: Number(dashMatch[1]),
          month: Number(dashMatch[2]),
          day: Number(dashMatch[3]),
        }
      : null;

  if (!parts) return null;

  const parsed = new Date(parts.year, parts.month - 1, parts.day);
  const isValid =
    parsed.getFullYear() === parts.year &&
    parsed.getMonth() === parts.month - 1 &&
    parsed.getDate() === parts.day;

  return isValid ? parsed : null;
};

const resolveDateInputValue = (date, keyboardValue) => {
  if (date instanceof Date && !Number.isNaN(date.getTime())) return formatForInput(date);
  if (keyboardValue === "") return "";

  const parsedTypedDate = parseTypedDate(keyboardValue);
  return parsedTypedDate ? formatForInput(parsedTypedDate) : null;
};

export default function FiltersBar({
  mode, setMode,
  accrualField, setAccrualField,
  rangePreset, setRangePreset,           // '30d' | '90d' | 'ytd'
  useCustom, setUseCustom,
  fromCustom, setFromCustom,
  toCustom, setToCustom,
  statusIn, setStatusIn,
  modeLocked = false,
  showStatusFilters = true,
  grouping, setGrouping,                  // 'day' | 'week' | 'month'
  loading = false,
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const activeGrouping = grouping || (rangePreset === "ytd" ? "month" : "day");
  const compactButtonStyle = isMobile
    ? { minWidth: 0, padding: "4px 7px", fontSize: 11 }
    : undefined;
  const pendingScrollRef = React.useRef(null);
  const restoreTimersRef = React.useRef([]);
  const clearRestoreTimers = React.useCallback(() => {
    if (typeof window === "undefined") return;
    restoreTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    restoreTimersRef.current = [];
  }, []);
  const restoreMobileScroll = React.useCallback(() => {
    if (!isMobile || typeof window === "undefined" || !pendingScrollRef.current) return;
    const { scrollX, scrollY } = pendingScrollRef.current;
    window.scrollTo(scrollX, scrollY);
  }, [isMobile]);
  React.useEffect(() => () => clearRestoreTimers(), [clearRestoreTimers]);
  React.useEffect(() => {
    if (!isMobile || loading || !pendingScrollRef.current || typeof window === "undefined") return undefined;

    clearRestoreTimers();
    restoreTimersRef.current = [0, 80, 220].map((delay, index, delays) =>
      window.setTimeout(() => {
        restoreMobileScroll();
        if (index === delays.length - 1) pendingScrollRef.current = null;
      }, delay)
    );

    return () => clearRestoreTimers();
  }, [clearRestoreTimers, isMobile, loading, restoreMobileScroll]);
  const preserveMobileScroll = React.useCallback((action) => {
    if (!isMobile || typeof window === "undefined") {
      action();
      return;
    }

    pendingScrollRef.current = { scrollX: window.scrollX, scrollY: window.scrollY };
    action();
    clearRestoreTimers();
    restoreTimersRef.current = [0, 50, 150].map((delay) =>
      window.setTimeout(restoreMobileScroll, delay)
    );
  }, [clearRestoreTimers, isMobile, restoreMobileScroll]);
  const clearCustom = () => preserveMobileScroll(() => { setUseCustom(false); setFromCustom(""); setToCustom(""); });
  const updateDateValue = (setter) => (date, keyboardValue) => {
    const nextValue = resolveDateInputValue(date, keyboardValue);
    if (nextValue === null) return;

    preserveMobileScroll(() => {
      setUseCustom(true);
      setter(nextValue);
    });
  };

  const toggleChip = (label) => {
    const active = statusIn.includes(label);
    return (
      <Chip
        key={label}
        label={label}
        size="small"
        color={active ? "primary" : "default"}
        variant={active ? "default" : "outlined"}
        onClick={() =>
          preserveMobileScroll(() => setStatusIn(prev => active ? prev.filter(x => x !== label) : [...prev, label]))
        }
        style={{ borderRadius: 999 }}
      />
    );
  };

  const modeControls = (
    <ButtonGroup size="small" variant="outlined" disabled={modeLocked}>
      <Button style={compactButtonStyle} variant={mode === "cash" ? "contained" : "outlined"} onClick={() => preserveMobileScroll(() => setMode("cash"))}>Cash</Button>
      <Button style={compactButtonStyle} variant={mode === "accrual" ? "contained" : "outlined"} onClick={() => preserveMobileScroll(() => setMode("accrual"))}>Accrual</Button>
    </ButtonGroup>
  );

  const rangeControls = (
    <ButtonGroup size="small" variant="outlined" disabled={useCustom}>
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setRangePreset("30d"))} variant={!useCustom && rangePreset === "30d" ? "contained" : "outlined"}>30d</Button>
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setRangePreset("90d"))} variant={!useCustom && rangePreset === "90d" ? "contained" : "outlined"}>90d</Button>
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setRangePreset("ytd"))} variant={!useCustom && rangePreset === "ytd" ? "contained" : "outlined"}>YTD</Button>
    </ButtonGroup>
  );

  const groupingControls = (
    <ButtonGroup size="small" variant="outlined">
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setGrouping?.("day"))} variant={activeGrouping === "day" ? "contained" : "outlined"}>Day</Button>
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setGrouping?.("week"))} variant={activeGrouping === "week" ? "contained" : "outlined"}>Week</Button>
      <Button style={compactButtonStyle} onClick={() => preserveMobileScroll(() => setGrouping?.("month"))} variant={activeGrouping === "month" ? "contained" : "outlined"}>Month</Button>
    </ButtonGroup>
  );

  const customToggle = (
    <Tooltip title={useCustom ? "Using custom range" : "Use custom range"}>
      <IconButton color={useCustom ? "primary" : "default"} onClick={() => preserveMobileScroll(() => setUseCustom(v => !v))} size="small">
        <CalendarTodayIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const clearCustomButton = useCustom ? (
    <Tooltip title="Clear custom range">
      <IconButton onClick={clearCustom} size="small">
        <ClearIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const statusControls = showStatusFilters ? (
    <Grid container spacing={1} justify="center">
      {["Paid", "Partial"].map(label => (
        <Grid item key={label}>{toggleChip(label)}</Grid>
      ))}
    </Grid>
  ) : null;

  const pickerInputProps = compactButtonStyle
    ? {
        inputProps: {
          style: {
            fontSize: 13,
            padding: "10px 0 10px 8px",
          },
        },
        KeyboardButtonProps: {
          size: "small",
          style: { padding: 4 },
        },
        InputAdornmentProps: {
          style: { marginLeft: 0 },
        },
      }
    : {};

  const datePickers = (fullWidth = false, compact = false) => {
    const pickerStyle = compact
      ? { flex: "1 1 0", minWidth: 144, maxWidth: 160, marginTop: 0, marginBottom: 0 }
      : undefined;
    const pickerGap = compact ? (
      <Box display="flex" alignItems="center" style={{ gap: 6, flex: "1 1 auto", minWidth: 0 }}>
        <KeyboardDatePicker
          autoOk
          variant="inline"
          inputVariant="outlined"
          format="MM/dd/yyyy"
          mask="__/__/____"
          margin="dense"
          label="From"
          value={parseInputDate(fromCustom)}
          onChange={updateDateValue(setFromCustom)}
          disabled={!useCustom}
          KeyboardButtonProps={{ "aria-label": "change start date", ...pickerInputProps.KeyboardButtonProps }}
          InputAdornmentProps={pickerInputProps.InputAdornmentProps}
          inputProps={pickerInputProps.inputProps}
          size="small"
          style={pickerStyle}
        />
        <KeyboardDatePicker
          autoOk
          variant="inline"
          inputVariant="outlined"
          format="MM/dd/yyyy"
          mask="__/__/____"
          margin="dense"
          label="To"
          value={parseInputDate(toCustom)}
          onChange={updateDateValue(setToCustom)}
          disabled={!useCustom}
          KeyboardButtonProps={{ "aria-label": "change end date", ...pickerInputProps.KeyboardButtonProps }}
          InputAdornmentProps={pickerInputProps.InputAdornmentProps}
          inputProps={pickerInputProps.inputProps}
          size="small"
          style={pickerStyle}
        />
      </Box>
    ) : (
      <>
        <KeyboardDatePicker
          autoOk
          variant="inline"
          inputVariant="outlined"
          format="MM/dd/yyyy"
          margin="dense"
          label="From"
          value={parseInputDate(fromCustom)}
          onChange={updateDateValue(setFromCustom)}
          disabled={!useCustom}
          KeyboardButtonProps={{ "aria-label": "change start date" }}
          size="small"
          fullWidth={fullWidth}
        />
        {!fullWidth ? <Typography variant="body2">to</Typography> : null}
        <KeyboardDatePicker
          autoOk
          variant="inline"
          inputVariant="outlined"
          format="MM/dd/yyyy"
          margin="dense"
          label="To"
          value={parseInputDate(toCustom)}
          onChange={updateDateValue(setToCustom)}
          disabled={!useCustom}
          KeyboardButtonProps={{ "aria-label": "change end date" }}
          size="small"
          fullWidth={fullWidth}
        />
      </>
    );

    return <MuiPickersUtilsProvider utils={DateFnsUtils}>{pickerGap}</MuiPickersUtilsProvider>;
  };

  if (isMobile) {
    return (
      <Card variant="outlined">
        <CardContent style={{ padding: 12 }}>
          <Box display="flex" flexDirection="column" alignItems="stretch" style={{ gap: 10 }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ gap: 6, overflowX: "auto", paddingBottom: 2 }}
            >
              {modeControls}
              {rangeControls}
              {groupingControls}
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ gap: 6, overflowX: "auto", paddingBottom: 2 }}
            >
              {customToggle}
              {clearCustomButton}
              {datePickers(false, true)}
            </Box>
            {statusControls ? <Box display="flex" justifyContent="center">{statusControls}</Box> : null}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={2} alignItems="center" justify="space-between">
          <Grid item>{modeControls}</Grid>

          <Grid item style={{ flex: 1 }}>
            <Grid container spacing={1} alignItems="center" justify="flex-end">
              <Grid item>{rangeControls}</Grid>

              <Grid item>
                <Divider orientation="vertical" style={{ height: 28 }} />
              </Grid>

              <Grid item>{groupingControls}</Grid>

              <Grid item>
                <Divider orientation="vertical" style={{ height: 28 }} />
              </Grid>

              <Grid item>{customToggle}</Grid>

              <Grid item>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  {datePickers(false)}
                </Box>
              </Grid>

              {useCustom && <Grid item>{clearCustomButton}</Grid>}

              {showStatusFilters && (
                <>
                  <Grid item>
                    <Divider orientation="vertical" style={{ height: 28 }} />
                  </Grid>

                  <Grid item>{statusControls}</Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
